// ante-react-kit/src/pos/AntCashShiftPopup/index.js
// Popup vào ca / cắt ca cho POS — frosted glass + 6-PIN, hiển thị két, số dư hệ thống, tiền thực kiểm.
import React, { Fragment, useEffect, useState } from 'react';
import { NumberBox } from 'devextreme-react';
import AntPasscodeOverlay from '../../components/AntPasscodeOverlay';
import AntAuth from '../../utils/AntAuth';

// Helper i18n đơn giản (tránh phụ thuộc utils/i18n không tồn tại)
const i18n = (o) => (o && (o.vi || o.en)) || '';

export default function AntCashShiftPopup(props) {
    const { mode, session, onDone, onClose } = props;
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [errorText, setErrorText] = useState('');
    const [errorKey, setErrorKey] = useState(0);
    const [prepData, setPrepData] = useState(null);
    const [actualAmt, setActualAmt] = useState(0);
    const [employeeCode, setEmployeeCode] = useState(null);
    const employees = (session && session.employee_codes) || [];

    useEffect(() => {
        const api = mode === 'checkin' ? 'prepareEmployeeCheckin' : 'prepareEmployeeCheckout';
        AntAuth.callApi('user/ACUserAuthen/' + api, {}, (res) => {
            const data = (res && res.data) || {};
            setPrepData(data);
            setActualAmt(Number(data.system_amt) || 0);
            setEmployeeCode(employees.length === 1 ? employees[0] : null);
            setLoading(false);
        });
    }, [mode]);

    const handleComplete = (passCode) => {
        if (!employeeCode && mode === 'checkin') {
            setErrorText(i18n({ vi: 'Chọn nhân viên trước', en: 'Select employee first', fr: 'Choisir d\'abord l\'employé', es: 'Elija primero el empleado', id: 'Pilih karyawan dulu', ja: '先に従業員を選択', zh: '请先选择员工', ko: '먼저 직원을 선택하세요', hi: 'पहले कर्मचारी चुनें', ar: 'اختر الموظف أولًا' })); setErrorKey((k) => k + 1);
            return;
        }
        setProcessing(true);
        const cb = (res) => {
            setProcessing(false);
            if (!res || res.error) {
                const msg = (res && res.error && res.error.message && (res.error.message.vi || res.error.message.en)) || 'Lỗi';
                setErrorText(msg); setErrorKey((k) => k + 1);
                return;
            }
            if (onDone) onDone(res && res.data);
        };
        if (mode === 'checkin') {
            // checkinEmployee(empCode, passCode, data, done)
            AntAuth.checkinEmployee(
                employeeCode,
                passCode,
                {
                    cash_account_code: prepData.cash_account_code,
                    actual_amt: actualAmt,
                    store_code: session && session.store_code
                },
                cb
            );
        } else {
            // checkoutEmployee(passCode, data, done)
            AntAuth.checkoutEmployee(passCode, { actual_amt: actualAmt }, cb);
        }
    };

    if (loading) {
        return (
            <AntPasscodeOverlay
                title={mode === 'checkin' ? 'Vào ca' : 'Cắt ca'}
                processing={true}
                dismissible={true}
                onDismiss={onClose}
            />
        );
    }

    const currency = (prepData && prepData.currency) || '';
    const fmt = (n) => new Intl.NumberFormat().format(Math.round(Number(n) || 0)) + (currency ? ' ' + currency : '');

    const diff = mode === 'checkout' ? Number(actualAmt) - Number(prepData.system_amt || 0) : 0;
    const diffChip =
        mode === 'checkout' && diff !== 0 ? (
            <div className={`apt-shift-diff-chip ${diff < 0 ? 'is-low' : 'is-high'}`}>
                {diff < 0 ? `Thiếu ${fmt(-diff)}` : `Thừa ${fmt(diff)}`}
            </div>
        ) : null;

    const topSlot = (
        <div className="apt-shift-top">
            {mode === 'checkin' && employees.length > 1 && (
                <div className="apt-shift-emp-tabs">
                    {employees.map((c) => (
                        <button
                            key={c}
                            type="button"
                            className={`apt-shift-emp-tab ${employeeCode === c ? 'is-active' : ''}`}
                            onClick={() => setEmployeeCode(c)}
                        >
                            {c}
                        </button>
                    ))}
                </div>
            )}
            <div className="apt-shift-row">
                <span className="apt-shift-label">Két</span>
                <span className="apt-shift-value">{prepData.cash_account_name || prepData.cash_account_code}</span>
            </div>
            {mode === 'checkout' && (
                <Fragment>
                    <div className="apt-shift-row">
                        <span className="apt-shift-label">Tổng thu trong ca</span>
                        <span className="apt-shift-value">+{fmt(prepData.total_received_in_shift)}</span>
                    </div>
                    <div className="apt-shift-row">
                        <span className="apt-shift-label">Tổng chi trong ca</span>
                        <span className="apt-shift-value">-{fmt(prepData.total_issued_in_shift)}</span>
                    </div>
                </Fragment>
            )}
            <div className="apt-shift-row">
                <span className="apt-shift-label">Số dư hệ thống</span>
                <span className="apt-shift-value">{fmt(prepData.system_amt)}</span>
            </div>
            <div className="apt-shift-row">
                <span className="apt-shift-label">{mode === 'checkin' ? 'Tiền thực nhận' : 'Tiền thực kiểm'}</span>
                <NumberBox value={actualAmt} onValueChanged={(e) => setActualAmt(e.value)} format="#,##0" width={180} />
            </div>
            {diffChip}
        </div>
    );

    const bottomActions = (
        <button type="button" className="apt-passcode-secondary-btn" onClick={onClose}>
            Huỷ
        </button>
    );

    return (
        <AntPasscodeOverlay
            title={mode === 'checkin' ? 'Vào ca' : 'Cắt ca'}
            subtitle={prepData.subtitle || (session && session.store_name)}
            topSlot={topSlot}
            bottomActions={bottomActions}
            processing={processing}
            errorText={errorText}
            errorKey={errorKey}
            onComplete={handleComplete}
            dismissible={true}
            onDismiss={onClose}
        />
    );
}
